import { ProfileCategory } from '@/types/profile';

export const STUDENT_PROFILE_CATEGORIES: ProfileCategory[] = [
  {
    key: 'basic_info',
    title: 'Basic Information',
    description: 'Essential personal details',
    weight: 25,
    icon: 'User',
    fields: [
      {
        key: 'first_name',
        label: 'First Name',
        category: 'basic_info',
        weight: 8,
        required: true,
        type: 'text',
        placeholder: 'Enter your first name'
      },
      {
        key: 'last_name',
        label: 'Last Name',
        category: 'basic_info',
        weight: 7,
        required: true,
        type: 'text',
        placeholder: 'Enter your last name'
      },
      {
        key: 'phone',
        label: 'Phone Number',
        category: 'basic_info',
        weight: 5,
        required: false,
        type: 'tel',
        placeholder: 'e.g., +1 (555) 000-0000'
      },
      {
        key: 'date_of_birth',
        label: 'Date of Birth',
        category: 'basic_info',
        weight: 5,
        required: false,
        type: 'date',
        placeholder: 'DD-MM-YYYY',
        description: 'Enter your date of birth in DD-MM-YYYY format'
      },
      {
        key: 'email',
        label: 'Email Address',
        category: 'basic_info',
        weight: 6,
        required: true,
        type: 'email',
        placeholder: 'Enter your email address'
      }
    ]
  },
  {
    key: 'academic_info',
    title: 'Academic Information',
    description: 'Your educational background and goals',
    weight: 30,
    icon: 'GraduationCap',
    fields: [
      {
        key: 'student_details.class_grade',
        label: 'Class/Grade',
        category: 'academic_info',
        weight: 10,
        required: true,
        type: 'select',
        options: ['Grade 9', 'Grade 10'],
        placeholder: 'Select your current grade'
      },
      {
        key: 'student_details.school_name',
        label: 'School Name',
        category: 'academic_info',
        weight: 8,
        required: false,
        type: 'select',
        options: [
          'Lincoln High School',
          'Washington Middle School',
          'Roosevelt Elementary',
          'Jefferson Academic Institute',
          'Kennedy International School',
          'Franklin STEM Academy',
          'Madison Arts High School',
          'Jackson Public School',
          'Hamilton Preparatory School',
          'Adams Community School',
          'Wilson Charter School',
          'Monroe Technical Institute',
          'Taylor International Academy',
          'Johnson Memorial High School',
          'Brown University Prep',
          'Davis Science Academy',
          'Miller Arts & Technology School',
          'Anderson Global School',
          'Thomas Edison High School',
          'Martin Luther King Jr. Academy',
          'Other'
        ],
        placeholder: 'Select your school'
      },
      {
        key: 'student_details.academic_interests',
        label: 'Academic Interests',
        category: 'academic_info',
        weight: 7,
        required: false,
        type: 'multiselect',
        options: ['Mathematics', 'Science', 'English', 'History', 'Geography', 'Arts', 'Music', 'Sports', 'Technology', 'Languages'],
        description: 'Select subjects you are most interested in'
      },
      {
        key: 'student_details.learning_goals',
        label: 'Learning Goals',
        category: 'academic_info',
        weight: 5,
        required: false,
        type: 'multiselect',
        options: ['Improve Grades', 'Exam Preparation', 'Skill Development', 'Career Preparation', 'Personal Interest', 'Competitive Exams'],
        description: 'What do you want to achieve through learning?'
      }
    ]
  },
  {
    key: 'preferences',
    title: 'Learning Preferences',
    description: 'How you prefer to learn and study',
    weight: 20,
    icon: 'Settings',
    fields: [
      {
        key: 'student_details.learning_style',
        label: 'Learning Style',
        category: 'preferences',
        weight: 8,
        required: false,
        type: 'select',
        options: ['Visual (diagrams, charts)', 'Auditory (listening, discussions)', 'Kinesthetic (hands-on activities)', 'Reading/Writing (text-based)'],
        description: 'How do you learn best?'
      },
      {
        key: 'student_details.preferred_subjects',
        label: 'Preferred Subjects',
        category: 'preferences',
        weight: 7,
        required: false,
        type: 'multiselect',
        options: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography', 'Computer Science'],
        description: 'Subjects you enjoy studying most'
      },
      {
        key: 'student_details.study_schedule',
        label: 'Preferred Study Time',
        category: 'preferences',
        weight: 5,
        required: false,
        type: 'select',
        options: ['Early Morning (6-9 AM)', 'Morning (9-12 PM)', 'Afternoon (12-4 PM)', 'Evening (4-8 PM)', 'Night (8-11 PM)'],
        description: 'When do you study most effectively?'
      }
    ]
  },
  {
    key: 'personal_info',
    title: 'Personal Information',
    description: 'Additional details about yourself',
    weight: 15,
    icon: 'Heart',
    fields: [
      {
        key: 'avatar_url',
        label: 'Profile Picture',
        category: 'personal_info',
        weight: 6,
        required: false,
        type: 'avatar',
        description: 'Upload a professional photo (JPG, PNG, WebP • Max 2MB)'
      },
      {
        key: 'bio',
        label: 'Bio',
        category: 'personal_info',
        weight: 5,
        required: false,
        type: 'textarea',
        placeholder: 'Tell us about yourself...',
        description: 'Your chance to shine! Share your story, hobbies, superpowers, or why you think pineapple belongs on pizza 🍕'
      },
      {
        key: 'location',
        label: 'Location',
        category: 'personal_info',
        weight: 4,
        required: false,
        type: 'text',
        placeholder: 'City, Country',
        description: 'Where are you located?'
      }
    ]
  },
  {
    key: 'additional_info',
    title: 'Additional Information',
    description: 'Achievements and activities',
    weight: 10,
    icon: 'Award',
    fields: [
      {
        key: 'student_details.achievements',
        label: 'Achievements',
        category: 'additional_info',
        weight: 6,
        required: false,
        type: 'multiselect',
        options: ['Academic Excellence', 'Sports Achievement', 'Arts & Culture', 'Leadership', 'Community Service', 'Technical Skills'],
        description: 'Your notable achievements'
      },
      {
        key: 'student_details.extracurricular_activities',
        label: 'Extracurricular Activities',
        category: 'additional_info',
        weight: 4,
        required: false,
        type: 'multiselect',
        options: ['Sports', 'Music', 'Drama', 'Debate', 'Science Club', 'Math Club', 'Art Club', 'Volunteer Work'],
        description: 'Activities you participate in outside academics'
      }
    ]
  }
];

export const TEACHER_PROFILE_CATEGORIES: ProfileCategory[] = [
  {
    key: 'basic_info',
    title: 'Basic Information',
    description: 'Essential personal details',
    weight: 20,
    icon: 'User',
    fields: [
      {
        key: 'first_name',
        label: 'First Name',
        category: 'basic_info',
        weight: 6,
        required: true,
        type: 'text',
        placeholder: 'Enter your first name'
      },
      {
        key: 'last_name',
        label: 'Last Name',
        category: 'basic_info',
        weight: 6,
        required: true,
        type: 'text',
        placeholder: 'Enter your last name'
      },
      {
        key: 'phone',
        label: 'Phone Number',
        category: 'basic_info',
        weight: 4,
        required: false,
        type: 'tel',
        placeholder: '+1 (555) 123-4567'
      },
      {
        key: 'date_of_birth',
        label: 'Date of Birth',
        category: 'basic_info',
        weight: 4,
        required: false,
        type: 'date',
        placeholder: 'DD-MM-YYYY',
        description: 'Enter your date of birth in DD-MM-YYYY format'
      },
      {
        key: 'email',
        label: 'Email Address',
        category: 'basic_info',
        weight: 6,
        required: true,
        type: 'email',
        placeholder: 'Enter your email address'
      }
    ]
  },
  {
    key: 'professional_info',
    title: 'Professional Information',
    description: 'Your qualifications and experience',
    weight: 35,
    icon: 'Briefcase',
    fields: [
      {
        key: 'teacher_details.qualifications',
        label: 'Qualifications',
        category: 'professional_info',
        weight: 10,
        required: true,
        type: 'multiselect',
        options: ['Bachelor\'s Degree', 'Master\'s Degree', 'PhD', 'Teaching Certificate', 'Subject Specialization', 'Professional Certification'],
        description: 'Your educational qualifications'
      },
      {
        key: 'teacher_details.years_of_experience',
        label: 'Years of Experience',
        category: 'professional_info',
        weight: 8,
        required: true,
        type: 'number',
        placeholder: 'e.g., 5',
        description: 'Total years of teaching experience'
      },
      {
        key: 'teacher_details.subjects_teaching',
        label: 'Subjects Teaching',
        category: 'professional_info',
        weight: 9,
        required: true,
        type: 'multiselect',
        options: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography', 'Computer Science', 'Arts', 'Physical Education'],
        description: 'Subjects you currently teach'
      },
      {
        key: 'teacher_details.institution_name',
        label: 'Institution Name',
        category: 'professional_info',
        weight: 8,
        required: false,
        type: 'select',
        options: [
          'Lincoln High School',
          'Washington Middle School',
          'Roosevelt Elementary',
          'Jefferson Academic Institute',
          'Kennedy International School',
          'Franklin STEM Academy',
          'Madison Arts High School',
          'Jackson Public School',
          'Hamilton Preparatory School',
          'Adams Community School',
          'Wilson Charter School',
          'Monroe Technical Institute',
          'Taylor International Academy',
          'Johnson Memorial High School',
          'Brown University Prep',
          'Davis Science Academy',
          'Miller Arts & Technology School',
          'Anderson Global School',
          'Thomas Edison High School',
          'Martin Luther King Jr. Academy',
          'Springfield University',
          'Riverside College',
          'Oakwood Institute of Technology',
          'Greenfield Community College',
          'Sunset Training Center',
          'Mountain View Academy',
          'Valley Educational Institute',
          'Hillcrest Learning Center',
          'Oceanside School District',
          'Pine Ridge Educational Foundation',
          'Other'
        ],
        placeholder: 'Select your institution'
      }
    ]
  },
  {
    key: 'teaching_profile',
    title: 'Teaching Profile',
    description: 'Your teaching approach and expertise',
    weight: 25,
    icon: 'BookOpen',
    fields: [
      {
        key: 'teacher_details.teaching_style',
        label: 'Teaching Style',
        category: 'teaching_profile',
        weight: 8,
        required: false,
        type: 'multiselect',
        options: ['Interactive Learning', 'Project-Based', 'Traditional Lectures', 'Hands-on Activities', 'Technology-Enhanced', 'Collaborative Learning'],
        description: 'How do you prefer to teach?'
      },
      {
        key: 'teacher_details.specializations',
        label: 'Specializations',
        category: 'teaching_profile',
        weight: 9,
        required: false,
        type: 'multiselect',
        options: ['Special Education', 'Advanced Placement', 'STEM Education', 'Language Arts', 'Early Childhood', 'Adult Education'],
        description: 'Your teaching specializations'
      },
      {
        key: 'teacher_details.certifications',
        label: 'Certifications',
        category: 'teaching_profile',
        weight: 8,
        required: false,
        type: 'multiselect',
        options: ['Teaching License', 'Subject Certification', 'Technology Integration', 'Assessment & Evaluation', 'Classroom Management'],
        description: 'Professional certifications you hold'
      }
    ]
  },
  {
    key: 'personal_info',
    title: 'Personal Information',
    description: 'Additional details about yourself',
    weight: 12,
    icon: 'Heart',
    fields: [
      {
        key: 'avatar_url',
        label: 'Profile Picture',
        category: 'personal_info',
        weight: 5,
        required: false,
        type: 'avatar',
        description: 'Upload a professional photo (JPG, PNG, WebP • Max 2MB)'
      },
      {
        key: 'bio',
        label: 'Bio',
        category: 'personal_info',
        weight: 4,
        required: false,
        type: 'textarea',
        placeholder: 'Tell us about your teaching philosophy...',
        description: 'Share your teaching journey! What makes you tick? Coffee addiction? Secret ninja skills? We want to know! ☕️🥷'
      },
      {
        key: 'location',
        label: 'Location',
        category: 'personal_info',
        weight: 3,
        required: false,
        type: 'text',
        placeholder: 'City, Country'
      }
    ]
  },
  {
    key: 'additional_info',
    title: 'Additional Information',
    description: 'Awards and professional development',
    weight: 8,
    icon: 'Award',
    fields: [
      {
        key: 'teacher_details.awards',
        label: 'Awards & Recognition',
        category: 'additional_info',
        weight: 3,
        required: false,
        type: 'multiselect',
        options: ['Teacher of the Year', 'Excellence in Education', 'Innovation in Teaching', 'Student Impact Award', 'Research Recognition'],
        description: 'Awards and recognitions received'
      },
      {
        key: 'teacher_details.publications',
        label: 'Publications',
        category: 'additional_info',
        weight: 2,
        required: false,
        type: 'multiselect',
        options: ['Research Papers', 'Educational Articles', 'Teaching Resources', 'Books/Chapters', 'Conference Presentations'],
        description: 'Your published works'
      },
      {
        key: 'teacher_details.professional_development',
        label: 'Professional Development',
        category: 'additional_info',
        weight: 3,
        required: false,
        type: 'multiselect',
        options: ['Workshops Attended', 'Conferences', 'Online Courses', 'Mentoring Programs', 'Professional Organizations'],
        description: 'Recent professional development activities'
      }
    ]
  }
];

export const getProfileCategories = (userType: 'student' | 'teacher'): ProfileCategory[] => {
  return userType === 'student' ? STUDENT_PROFILE_CATEGORIES : TEACHER_PROFILE_CATEGORIES;
};
